import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import { DataGrid, GridEditInputCell } from 'Components/LifeDataGrid'
import { styled } from '@mui/material/styles'
import { makePostRequest } from 'utils/api'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}))

function NameEditInputCell(props) {
  const { error } = props

  return (
    <StyledTooltip open={error ? true : false} title={error}>
      <GridEditInputCell {...props} />
    </StyledTooltip>
  )
}

function renderEditName(params) {
  return <NameEditInputCell {...params} />
}

function ListOperations({ fetchOperations, operations, setSelectedRows, classificationSelected }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useTranslation('layout') // LAP-733 (GE022)

  const [rows, setRows] = useState(20)
  const [page, setPage] = useState(0)

  const [pageUrl, setPageUrl] = useState('')
  const [modifiedColumns, setModifiedColumns] = useState([])

  useEffect(() => {
    // setTTs(!tts)
    if (isMobile) {
      setModifiedColumns([
        {
          field: 'SousCategorie',
          headerName: t('finance.monBudget.columnSubCategory'),
          flex: 1,
          minWidth: 180,
          renderCell: (params) => (
            <Box py={2} mt={1}>
              <Box fontWeight={600}>{params.row.SousCategorie}</Box>
              <Box>
                <Box component={'span'}>{t('finance.monBudget.inlineCategories')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.Categorie}
                </Box>
              </Box>
              <Box>
                <Box component={'span'}>{t('finance.monBudget.inlineClassification')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.Classification}
                </Box>
              </Box>
              <Box>
                <Box component={'span'}>{t('finance.monBudget.inlineDomain')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.Domaine}
                </Box>
              </Box>
            </Box>
          ),
        },
        {
          field: 'PartActuel',
          headerName: t('finance.monBudget.columnPart'),
          flex: 1,
          width: 70,
          renderCell: (params) => <Box py={2}>{params.row.PartActuel} %</Box>,
        },
        {
          field: 'PartCible',
          headerName: t('finance.monBudget.columnPartCible'),
          flex: 1,
          width: 90,
          editable: true,
          preProcessEditCellProps,
          renderEditCell: renderEditName,
        },
      ])
    } else {
      setModifiedColumns(columns)
    }
  }, [isMobile])

  useEffect(() => {
    setPageUrl('?Classification=' + classificationSelected.label)
  }, [classificationSelected])

  const handleRowsChange = (newPageSize) => {
    setRows(newPageSize)
    let url = `?limit=${newPageSize}&offset=${page}&Classification=${classificationSelected.label}`
    setPageUrl(url)
    fetchOperations(url)
  }
  const handlePageChange = (newPage) => {
    setPage(newPage * rows)
    let url = `?limit=${rows}&offset=${newPage * rows}&Classification=${
      classificationSelected.label
    }`
    setPageUrl(url)

    fetchOperations(url)
  }
  const preProcessEditCellProps = async (params) => {
    const payload = {
      id: params.id,
      value: params.props.value ? params.props.value : 0,
    }
    let errorMessage = ''
    // if (params.props.value) {
    const response = await makePostRequest('/api/Finance/part-cible-check/', payload, {}, true)
    if (response.status === 200) {
      errorMessage = response.data
      if (!errorMessage) {
        await fetchOperations(pageUrl)
      }
    }
    // }

    return { ...params.props, error: errorMessage }
  }

  const columns = [
    {
      field: 'Operations',
      headerName: t('finance.monBudget.columnOperation'),
      width: 140,
      valueGetter: (params) => (params.row.Operations === 'D' ? t('finance.monBudget.typeExpense') : t('finance.monBudget.typeIncome')),
      editable: false,
    },
    {
      field: 'Categorie',
      headerName: t('finance.monBudget.inlineCategories'),
      width: 150,
      editable: false,
    },
    {
      field: 'SousCategorie',
      headerName: t('finance.monBudget.columnSubCategory'),
      width: 160,
      editable: false,
      renderCell: (params) => params.row.SousCategorie,
    },
    {
      field: 'Classification',
      headerName: t('finance.monBudget.columnClassification'),
      width: 150,
      editable: false,
    },
    {
      field: 'Domaine',
      headerName: t('finance.monBudget.columnDomain'),
      width: 140,
      editable: false,
    },
    {
      field: 'PartActuel',
      headerName: t('finance.monBudget.columnPartActuel'),
      width: 100,
      editable: false,
      // valueGetter: (params) => `${params.row.PartActuel} %`,
      renderCell: (params) => <>{params.row.PartActuel} %</>,
    },
    {
      field: 'PartCible',
      headerName: t('finance.monBudget.columnPartCible'),
      width: 100,
      editable: true,
      type: 'number',
      preProcessEditCellProps,
      renderEditCell: renderEditName,
    },
    {
      field: 'MontantCible',
      headerName: t('finance.monBudget.columnMontantCible'),
      width: 100,
      editable: false,
    },
  ]
  function DataGridTitle() {
    return (
      <Box style={{ width: '100%', display: 'flex' }} mt={2}>
        <Box
          sx={{ color: 'black !important', fontWeight: 700, marginLeft: 2 }}
          fontSize={isMobile ? 18 : 22}
        >
          {t('finance.monBudget.subcategoriesCount', { count: operations.count ? operations.count : 0 })}
        </Box>
      </Box>
    )
  }

  return (
    <div
      style={{
        width: '100% ',
        background: 'white',
        borderRadius: '5px',
      }}
    >
      <DataGrid
        sx={{
          '.MuiTablePagination-toolbar': {
            alignItems: 'baseline',
          },
          height: 800,
          fontSize: isMobile ? '11px' : '13px',
          '.dtcell': { alignItems: 'flex-start' },
        }}
        rows={operations.results ? operations.results : []}
        columns={modifiedColumns}
        getRowHeight={() => (isMobile ? 'auto' : 50)}
        pageSize={rows}
        rowCount={operations.count ? operations.count : 0}
        onPageSizeChange={handleRowsChange}
        onPageChange={handlePageChange}
        rowsPerPageOptions={[10, 20, 40, 80, 100]}
        pagination
        onSelectionModelChange={(itm) => setSelectedRows([...itm])}
        paginationMode="server"
        // disableSelectionOnClick
        // checkboxSelection
        disableSelectionOnClick
        components={{ Toolbar: DataGridTitle }}
      />
    </div>
  )
}
export default ListOperations
